<template>
  <div class="card-choice">
    <div v-for="item in items" class="card-choice-item" :key="item.kind">
      <div class="card-choice-thumb">
        <img v-if="item.url" :src="item.url" :alt="item.label" />
        <div v-else class="card-choice-placeholder">
          <i v-if="item.state === 'loading'" class="fa-solid fa-spinner fa-spin" />
          <span v-else>{{ item.state === 'missing' ? 'Pas encore reçue de la FFD' : 'Carte indisponible' }}</span>
        </div>
      </div>
      <span class="card-choice-label">{{ item.label }}</span>
      <button class="card-choice-btn" :disabled="!item.url || downloading === item.kind" @click="emit('download', item.kind)">
        <i class="fa-solid" :class="downloading === item.kind ? 'fa-spinner fa-spin' : 'fa-download'" /> Télécharger
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// Les deux cartes d'une licence côte à côte, en miniature : l'officielle envoyée par la FFD
// (importée par le comité) et celle générée par DartsHub -- le licencié choisit laquelle
// télécharger. Les images sont préparées par la page (cf. ProfilePage.vue::getCardImage).
export type CardKind = 'ffd' | 'generated';
export interface CardChoiceItem {
  kind: CardKind;
  label: string;
  url: string | null;
  state: 'ready' | 'loading' | 'missing' | 'error';
}

defineProps<{ items: CardChoiceItem[]; downloading: CardKind | null }>();
const emit = defineEmits<{ download: [kind: CardKind] }>();
</script>

<style scoped>
.card-choice {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.card-choice-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

/* Cadre commun au format carte (85,6 x 54 mm) : la carte FFD (603 x 353) et la carte
   générée (~1011 x 640) n'ont pas les mêmes proportions, chacune y est centrée sans être
   déformée pour garder libellés et boutons alignés. */
.card-choice-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 85.6 / 54;
}

/* En absolu : une hauteur issue d'aspect-ratio ne borne pas un max-height en % dans un
   flux normal (la carte générée, plus haute, agrandissait son cadre). */
.card-choice-thumb img {
  position: absolute;
  inset: 0;
  margin: auto;
  display: block;
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.card-choice-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 8px;
  border: 1px dashed var(--border);
  border-radius: 6px;
  font-size: 0.75rem;
  text-align: center;
  color: var(--text-muted);
}

.card-choice-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-muted);
  text-align: center;
}

.card-choice-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  background: color-mix(in srgb, var(--primary) 8%, transparent);
  color: var(--primary);
  border: 1.5px solid color-mix(in srgb, var(--primary) 25%, transparent);
}

.card-choice-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--primary) 15%, transparent);
  border-color: var(--primary);
}

.card-choice-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
